<template>
  <div class="relative min-h-screen overflow-hidden">
    <!-- 背景装饰 -->
    <div class="fixed inset-0 bg-gradient-to-b from-primary-500/20 via-white to-gray-50/90 bg-gradient-animate"></div>

    <!-- 装饰圆形 -->
    <div class="fixed top-0 left-0 w-[1000px] h-[1000px] bg-gradient-to-r from-primary-500/30 to-purple-300/30 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2"></div>
    <div class="fixed top-1/2 right-0 w-[800px] h-[800px] bg-gradient-to-l from-blue-300/30 to-purple-400/30 rounded-full blur-[80px] translate-x-1/2"></div>
    <div class="fixed bottom-0 right-0 w-[900px] h-[900px] bg-gradient-to-tl from-primary-400/20 via-pink-300/20 to-purple-300/20 rounded-full blur-[90px] translate-x-1/3 translate-y-1/3"></div>

    <!-- 主要内容 -->
    <div class="relative">
      <LandingHeader />
      <!-- 价格标题区域 -->
      <section class="relative pt-32 pb-20">
        <div class="container mx-auto px-4 text-center">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
            {{ $t('web.pricing.title.main') }} <span class="text-primary-500">{{ $t('web.pricing.title.highlight') }}</span>
          </h1>
          <p class="text-gray-600 mb-8 max-w-2xl mx-auto text-lg">
            {{ $t('web.pricing.description') }}
          </p>
        </div>
      </section>

      <!-- 价格卡片区域 -->
      <section class="relative py-20">
        <div class="container mx-auto px-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- 基础版 -->
            <div class="group bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100/50">
              <div class="text-center mb-8">
                <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('web.pricing.plans.basic.name') }}</h3>
                <div class="mb-8">
                  <div class="flex items-center justify-center mb-4">
                    <span class="text-4xl font-bold">{{ $t('web.pricing.plans.basic.price') }}</span>
                    <span class="text-gray-500 ml-2">{{ $t('web.pricing.plans.basic.period') }}</span>
                  </div>
                  <p class="text-gray-600">{{ $t('web.pricing.plans.basic.description') }}</p>
                </div>
                <ul class="space-y-4 mb-8">
                  <li class="flex items-center gap-2">
                    <div class="w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center flex-shrink-0">
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span class="text-gray-600">{{ $t('web.pricing.plans.basic.features.apiCalls') }}</span>
                  </li>
                </ul>
              </div>
              <button class="w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-full hover:bg-gray-200 transition-colors">
                {{ $t('web.pricing.plans.basic.cta') }}
              </button>
            </div>

            <!-- 专业版 -->
            <div class="group bg-gradient-to-br from-primary-500 to-primary-600 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-primary-400/50 transform scale-105">
              <div class="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-4 py-1 rounded-full text-sm">
                {{ $t('web.pricing.plans.pro.badge') }}
              </div>
              <div class="text-center mb-8">
                <h3 class="text-2xl font-bold text-white mb-4">{{ $t('web.pricing.plans.pro.name') }}</h3>
                <div class="flex items-center justify-center mb-4">
                  <span class="text-4xl font-bold text-white">{{ $t('web.pricing.plans.pro.price') }}</span>
                  <span class="text-white/80 ml-2">{{ $t('web.pricing.plans.pro.period') }}</span>
                </div>
                <p class="text-white/80">{{ $t('web.pricing.plans.pro.description') }}</p>
              </div>
              <ul class="space-y-4 mb-8">
                <li class="flex items-center gap-2">
                  <div class="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span class="text-white">{{ $t('web.pricing.plans.pro.features.apiCalls') }}</span>
                </li>
                <li class="flex items-center gap-2">
                  <div class="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span class="text-white">{{ $t('web.pricing.plans.pro.features.analysis') }}</span>
                </li>
                <li class="flex items-center gap-2">
                  <div class="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span class="text-white">{{ $t('web.pricing.plans.pro.features.projects') }}</span>
                </li>
                <li class="flex items-center gap-2">
                  <div class="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <svg class="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <span class="text-white">{{ $t('web.pricing.plans.pro.features.support') }}</span>
                </li>
              </ul>
              <button class="w-full bg-white text-primary-500 px-6 py-3 rounded-full hover:bg-gray-50 transition-colors">
                {{ $t('web.pricing.plans.pro.cta') }}
              </button>
            </div>

            <!-- 企业版 -->
            <div class="group bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 border border-gray-100/50">
              <div class="text-center mb-8">
                <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('web.pricing.plans.enterprise.name') }}</h3>
                <div class="mb-8">
                  <div class="flex items-center justify-center mb-4">
                    <span class="text-4xl font-bold">{{ $t('web.pricing.plans.enterprise.price') }}</span>
                    <span class="text-gray-500 ml-2">{{ $t('web.pricing.plans.enterprise.period') }}</span>
                  </div>
                  <p class="text-gray-600">{{ $t('web.pricing.plans.enterprise.description') }}</p>
                </div>
                <ul class="space-y-4 mb-8">
                  <li class="flex items-center gap-2">
                    <div class="w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center flex-shrink-0">
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span class="text-gray-600">{{ $t('web.pricing.plans.enterprise.features.apiCalls') }}</span>
                  </li>
                </ul>
              </div>
              <button class="w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-full hover:bg-gray-200 transition-colors">
                {{ $t('web.pricing.plans.enterprise.cta') }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ区域 -->
      <section class="relative py-20">
        <div class="container mx-auto px-4">
          <h2 class="text-3xl font-bold text-center mb-12">{{ $t('web.pricing.faq.title') }}</h2>
          <div class="max-w-3xl mx-auto space-y-4">
            <div v-for="(faq, index) in faqs" :key="index" class="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
              <button
                @click="toggleFaq(index)"
                class="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50/50 transition-colors rounded-xl"
              >
                <span class="font-semibold text-gray-900">{{ faq.question }}</span>
                <span class="transform transition-transform" :class="{ 'rotate-180': faq.isOpen }">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              <div v-show="faq.isOpen" class="px-6 pb-4">
                <p class="text-gray-600">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <LandingFooter />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import LandingHeader from '@/components/layout/LandingHeader.vue'
import LandingFooter from '@/components/layout/LandingFooter.vue'

const { t } = useI18n()

const faqs = ref([
  {
    question: t('web.pricing.faq.questions.choosePlan.question'),
    answer: t('web.pricing.faq.questions.choosePlan.answer'),
    isOpen: false
  },
  {
    question: t('web.pricing.faq.questions.customPlan.question'),
    answer: t('web.pricing.faq.questions.customPlan.answer'),
    isOpen: false
  },
  {
    question: t('web.pricing.faq.questions.apiCount.question'),
    answer: t('web.pricing.faq.questions.apiCount.answer'),
    isOpen: false
  },
  {
    question: t('web.pricing.faq.questions.refund.question'),
    answer: t('web.pricing.faq.questions.refund.answer'),
    isOpen: false
  }
])

const toggleFaq = (index) => {
  faqs.value[index].isOpen = !faqs.value[index].isOpen
}
</script>

<style>
.bg-gradient-animate {
  background-size: 400% 400%;
  animation: gradient 15s ease infinite;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
